import { type MutationOptions } from '@tanstack/react-query';
import { Loader2, Send } from 'lucide-react';
import { useId, useState } from 'react';
import { useNotify, useStore, useTranslate } from 'ra-core';
import { useCurrentUser } from '../../auth/currentUserContext';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

/**
 * An "Export report" button that asks for the email addresses (separated by `;`, remembered for next time) and sends the report to them with `onSubmit`.
 */
export function ExportToEmailButton({
  onSubmit,
  isSubmitting,
}: {
  onSubmit: (
    destinationEmailAddresses: string[],
    options: MutationOptions<unknown, unknown, unknown>
  ) => void;
  isSubmitting: boolean;
}) {
  const [showModal, setShowModal] = useState(false);
  const user = useCurrentUser();
  const notify = useNotify();
  const translate = useTranslate();
  const inputId = useId();
  const helperTextId = useId();

  const [preferredEmail, setPreferredEmail] = useStore(
    'report-destination-email-address',
    user.email
  );

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => setShowModal(true)}
      >
        <Send />
        {translate('moonstar.export.email.button', { _: 'Export report' })}
      </Button>
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent>
          <form
            className="grid gap-4"
            onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.currentTarget);
              const email = formData.get('email') as string;

              setPreferredEmail(email);

              onSubmit(
                email.split(';').map((e) => e.trim()),
                {
                  onSuccess: () => {
                    setShowModal(false);
                    notify(
                      translate('moonstar.export.email.success', {
                        _: 'The email is on its way, and will be in your inbox shortly',
                      }),
                      { type: 'success' }
                    );
                  },
                  onError: (err) => {
                    console.error(err);
                    notify(
                      translate('moonstar.export.email.error', {
                        _: 'Failed to send email.',
                      }),
                      { type: 'error' }
                    );
                  },
                }
              );
            }}
          >
            <DialogHeader>
              <DialogTitle>
                {translate('moonstar.export.email.button', {
                  _: 'Export report',
                })}
              </DialogTitle>
              <DialogDescription className="sr-only">
                {translate('moonstar.export.email.description', {
                  _: 'Send the report by email',
                })}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-2">
              <Label htmlFor={inputId}>
                {translate('moonstar.export.email.recipients', {
                  _: 'Send report to',
                })}
              </Label>
              <Input
                id={inputId}
                name="email"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus
                defaultValue={preferredEmail ?? undefined}
                aria-describedby={helperTextId}
              />
              <p id={helperTextId} className="text-sm text-muted-foreground">
                {translate('moonstar.export.email.recipientsHelp', {
                  _: 'Separate multiple email addresses with a semicolon (;)',
                })}
              </p>
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowModal(false)}
              >
                {translate('ra.action.cancel')}
              </Button>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? <Loader2 className="animate-spin" /> : <Send />}
                {translate('moonstar.export.email.send', { _: 'Send' })}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
